import { Button } from '@quadralize/ui'
import { HomeAngle } from '@solar-icons/react'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

interface ErrorBlob {
  id: string
  color: string
  size: string
  top?: string
  left?: string
  right?: string
  bottom?: string
  delay: number
}

interface ErrorLayoutProps {
  code: string | number
  title: string
  description: string
  icon: ReactNode
  blobs?: ErrorBlob[]
  showHomeButton?: boolean
  actions?: ReactNode
  errorCode?: string
}

const ANIMATION_CONFIG = {
  MOUSE_RANGE: 200,
  ROTATE_X_MAX: 5,
  ROTATE_Y_MAX: 5,
  BLOB_Y_RANGE: 20,
  BLOB_X_RANGE: 15,
  BLOB_SCALE: 1.1,
  BLOB_DURATION: 10,
  RING_DUR_BASE: 15,
  RING_DUR_STEP: 5,
  RING_SIZE_BASE: 50,
  GRID_SIZE: 32,
  FULL_ROTATE: 360,
  SCALE_PULSE: 1.05,
  PULSE_DURATION: 4,
  RING_SCALES: {
    SMALL: 1.5,
    MEDIUM: 2.2,
    LARGE: 3,
  },
} as const

const DEFAULT_BLOBS: ErrorBlob[] = [
  {
    id: 'blob-1',
    color: 'bg-primary/20',
    size: 'w-48 h-48 md:w-80 md:h-80',
    top: '-5%',
    left: '-5%',
    delay: 0,
  },
  {
    id: 'blob-2',
    color: 'bg-secondary/15',
    size: 'w-56 h-56 md:w-96 md:h-96',
    bottom: '-10%',
    right: '-5%',
    delay: 2,
  },
  {
    id: 'blob-3',
    color: 'bg-warning/10',
    size: 'w-40 h-40 md:w-64 md:h-64',
    top: '30%',
    right: '10%',
    delay: 4,
  },
]

const RINGS = [
  { id: 'ring-1', scale: ANIMATION_CONFIG.RING_SCALES.SMALL },
  { id: 'ring-2', scale: ANIMATION_CONFIG.RING_SCALES.MEDIUM },
  { id: 'ring-3', scale: ANIMATION_CONFIG.RING_SCALES.LARGE },
]

export function ErrorLayout({
  code,
  title,
  description,
  icon,
  blobs = DEFAULT_BLOBS,
  showHomeButton = true,
  actions,
  errorCode,
}: ErrorLayoutProps) {
  const navigate = useNavigate()
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const mouseX = useSpring(x, { stiffness: 100, damping: 20 })
  const mouseY = useSpring(y, { stiffness: 100, damping: 20 })

  const rotateX = useTransform(
    mouseY,
    [-ANIMATION_CONFIG.MOUSE_RANGE, ANIMATION_CONFIG.MOUSE_RANGE],
    [ANIMATION_CONFIG.ROTATE_X_MAX, -ANIMATION_CONFIG.ROTATE_X_MAX],
  )
  const rotateY = useTransform(
    mouseX,
    [-ANIMATION_CONFIG.MOUSE_RANGE, ANIMATION_CONFIG.MOUSE_RANGE],
    [-ANIMATION_CONFIG.ROTATE_Y_MAX, ANIMATION_CONFIG.ROTATE_Y_MAX],
  )

  return (
    <div className="relative flex min-h-dvh w-full items-center justify-center overflow-hidden bg-bg-base px-4 py-8 md:py-12">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {blobs.map((blob) => (
          <motion.div
            key={blob.id}
            className={`absolute rounded-full blur-3xl ${blob.color} ${blob.size}`}
            style={{
              top: blob.top,
              left: blob.left,
              right: blob.right,
              bottom: blob.bottom,
            }}
            animate={{
              y: [0, ANIMATION_CONFIG.BLOB_Y_RANGE, 0],
              x: [0, ANIMATION_CONFIG.BLOB_X_RANGE, 0],
              scale: [1, ANIMATION_CONFIG.BLOB_SCALE, 1],
            }}
            transition={{
              duration: ANIMATION_CONFIG.BLOB_DURATION,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
              delay: blob.delay,
            }}
          />
        ))}

        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(to right, currentColor 1px, transparent 1px)',
            backgroundSize: `${ANIMATION_CONFIG.GRID_SIZE}px ${ANIMATION_CONFIG.GRID_SIZE}px`,
          }}
        />
      </div>

      <div className="container relative z-10 mx-auto max-w-[90vw]">
        <motion.div
          style={{ rotateX, rotateY, perspective: 1000 }}
          className="flex flex-col items-center"
        >
          <div className="relative mb-8 flex scale-90 items-center justify-center md:mb-16 md:scale-100">
            <motion.h1
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 0.07, scale: 1 }}
              transition={{ duration: 1.5 }}
              className="-z-10 pointer-events-none absolute select-none font-black text-[8rem] text-text-primary tracking-tighter sm:text-[15rem] md:text-[25rem]"
            >
              {code}
            </motion.h1>

            <div className="relative z-10 flex flex-col items-center">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="relative"
              >
                {RINGS.map((ring, idx) => (
                  <motion.div
                    key={ring.id}
                    className="-translate-x-1/2 -translate-y-1/2 pointer-events-none absolute top-1/2 left-1/2 rounded-full border border-primary/20"
                    style={{
                      width: `${ring.scale * ANIMATION_CONFIG.RING_SIZE_BASE}px`,
                      height: `${ring.scale * ANIMATION_CONFIG.RING_SIZE_BASE}px`,
                    }}
                    animate={{
                      rotate:
                        idx % 2 === 0
                          ? ANIMATION_CONFIG.FULL_ROTATE
                          : -ANIMATION_CONFIG.FULL_ROTATE,
                      scale: [1, ANIMATION_CONFIG.SCALE_PULSE, 1],
                    }}
                    transition={{
                      rotate: {
                        duration:
                          ANIMATION_CONFIG.RING_DUR_BASE + idx * ANIMATION_CONFIG.RING_DUR_STEP,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: 'linear',
                      },
                      scale: {
                        duration: ANIMATION_CONFIG.PULSE_DURATION,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: 'easeInOut',
                      },
                    }}
                  />
                ))}

                <div className="relative flex items-center justify-center rounded-4xl border border-border-default/50 bg-bg-base/40 p-6 shadow-xl backdrop-blur-md md:rounded-[2.5rem] md:p-8">
                  <div className="-top-1 -left-1 absolute h-3 w-3 rounded-tl-lg border-primary border-t-2 border-l-2 md:h-4 md:w-4" />
                  <div className="-bottom-1 -right-1 absolute h-3 w-3 rounded-br-lg border-primary border-r-2 border-b-2 md:h-4 md:w-4" />

                  <div className="h-12 w-12 text-primary md:h-16 md:w-16 [&>svg]:h-full [&>svg]:w-full">
                    {icon}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          <div className="max-w-xl px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <h2 className="mb-4 bg-linear-to-b from-text-primary to-text-primary/60 bg-clip-text font-bold text-3xl text-transparent tracking-tight sm:text-4xl md:mb-6 md:text-6xl">
                {title}
              </h2>

              <p className="mb-8 text-base text-text-secondary leading-relaxed md:mb-12 md:text-xl">
                {description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex flex-col items-center justify-center gap-4 sm:flex-row"
            >
              {actions}

              {showHomeButton && (
                <Button
                  size="lg"
                  startIcon={<HomeAngle className="h-5 w-5" />}
                  onClick={() => navigate('/')}
                  variant={actions ? 'outline' : 'default'}
                  className={actions ? 'w-full sm:w-56' : undefined}
                >
                  Voltar ao início
                </Button>
              )}
            </motion.div>

            {errorCode && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                transition={{ delay: 1, duration: 1 }}
                className="mt-10 border-border-subtle border-t pt-6 md:mt-16 md:pt-8"
              >
                <div className="flex items-center justify-center gap-3 font-medium text-text-tertiary text-xs uppercase tracking-widest md:text-sm">
                  <span className="h-px w-6 bg-border-subtle md:w-8" />
                  Código do erro: {errorCode}
                  <span className="h-px w-6 bg-border-subtle md:w-8" />
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}
